import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Injectable, OnDestroy } from '@angular/core';
import { BaseRestService } from './base-rest.service';
import { AssessmentCategory, IAssessmentCategoryList } from '../models/assessment-category';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { AssessmentTemplate } from '../models/assessment-template';
import { AssessmentTemplateAssessmentCategoryLink } from '../models/assessment-template-category-link';
import { AssessmentTemplateCategoryService } from './assessment-template-category.service';
import { map, take } from 'rxjs/operators';
import { AutoUnsubscribe } from 'auto-unsubscribe.decorator';

@AutoUnsubscribe()
@Injectable({ providedIn: 'root'})
export class AssessmentCategoryService extends BaseRestService<AssessmentCategory, IAssessmentCategoryList> implements OnDestroy{

  private onAssessmentTemplateCategoriesChanged: Subscription;
  private changed: Subscription;

  constructor(http: HttpClient, protected router: Router, private assessmentTemplateCategoryService: AssessmentTemplateCategoryService) {
    super(AssessmentCategoryService, http, 'api/assessmentcategories', 'assessment category', router);

    // enable caching
    this.cacheEnabled = true;
  
    this.onAssessmentTemplateCategoriesChanged = this.assessmentTemplateCategoryService.onChanged.subscribe((links: AssessmentTemplateAssessmentCategoryLink[]) => {
      const l = links;
      const cache = this.getCache();
      const hydrate$ = [];
      cache.forEach((c) => {
        c.assessmentTemplateAssessmentCategoryLinks = l.filter((x) => x.assessmentCategoryId === c.id);
        hydrate$.push(this.hydrate(c));
      });
      if (hydrate$.length > 0) {
        forkJoin(hydrate$).pipe(take(1)).subscribe();
      }
    });

    this.changed = this.onChanged.subscribe((categories) => {
      this.assessmentTemplateCategoryService.categories = [...categories];
    });
  }

  ngOnDestroy(){
    this.onAssessmentTemplateCategoriesChanged?.unsubscribe();
    this.changed?.unsubscribe();
  }

  hydrate(item: AssessmentCategory): Observable<AssessmentCategory> {
    const hydrate$ = [];
    if ((item.assessmentTemplateAssessmentCategoryLinks) && (item.assessmentTemplateAssessmentCategoryLinks.length > 0)){
      item.assessmentTemplateAssessmentCategoryLinks.map((link) => {
        if (link){
          if (link.assessmentTemplateId && ((!link.assessmentTemplate) || (link.assessmentTemplate.id !== link.assessmentTemplateId))){
            hydrate$.push(this.assessmentTemplateCategoryService.getAssessmentTemplateById(link.assessmentTemplateId)
              .pipe(take(1), map((assessmentTemplate: AssessmentTemplate) => {
                link.assessmentTemplate = assessmentTemplate;
                return item;
            })));
          }
        }
      });
    }

    if (hydrate$.length > 0){
      return forkJoin(hydrate$).pipe(take(1), map((_) => {
        item.templates = this.getTemplatesAsString(item);
        return item;
      }));
    }
    else{
      item.templates = this.getTemplatesAsString(item);
      return of(item);
    }
  }

  getTemplatesAsString(item: AssessmentCategory): string{
    return (item.assessmentTemplateAssessmentCategoryLinks) ? item.assessmentTemplateAssessmentCategoryLinks
          .filter((i) => i.assessmentTemplate)
          .sort((a, b) => a.assessmentTemplate.name.localeCompare(b.assessmentTemplate.name))
          .map((i) => i.assessmentTemplate.name).join(', ') : '';
  }

  save(entity: AssessmentCategory, successMessage?: string) {
    entity.assessmentTemplateAssessmentCategoryLinks.forEach((i) => {
      i.assessmentCategory = undefined;
      i.assessmentTemplate = undefined;
    });
    entity.assessmentTypes = entity.assessmentTypes || [];
    entity.assessmentTypes.forEach((i) => {
      i.category = undefined;
    });
    super.save(entity, successMessage);
  }
}
